import { CheckCircle2, MessageCircle, Phone, Sparkles, ExternalLink, Award } from "lucide-react";
import { waLink, WA_MESSAGES, PHONE_DISPLAY, PHONE_TEL } from "@/lib/whatsapp";
import { SITE_URL } from "@/lib/site";
import SEO from "@/components/SEO";

const DHRUVA_IMG = "/dhruva.jpg";
const BEFORE_AFTER = "/before-after.png";
const TIMES_OF_INDIA_URL =
  "https://timesofindia.indiatimes.com/life-style/health-fitness/weight-loss/weight-loss-story-i-had-a-high-protein-diet-and-stopped-drinking-alcohol-lost-50-kilos/amp_etphotostory/96703829.cms";

const CREDENTIALS = [
  "3rd Generation Yoga Practitioner",
  "Advanced Vinyasa-Krama Yoga Teacher",
  "Disciple of Srivatsa Ramaswami — 30 years of direct study with Sri T. Krishnamacharya",
  "Lineage of T. Krishnamacharya — guru of B.K.S. Iyengar, Pattabhi Jois, Indra Devi & T.K.V. Desikachar",
];

const PERSON_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Dhruva Lingappa",
  jobTitle: "Founder & Transformation Coach",
  worksFor: {
    "@type": "Organization",
    name: "Weight Loss Gurukul",
    url: `${SITE_URL}/`,
  },
  image: `${SITE_URL}/dhruva.jpg`,
  url: `${SITE_URL}/about`,
  description:
    "Founder of Weight Loss Gurukul. Vinyasa-Krama yoga practitioner in the T. Krishnamacharya lineage. Transformed 264 lbs to 154 lbs and reversed hypothyroidism — now coaches 1-on-1 globally.",
  knowsAbout: [
    "Yogasana", "Pranayama", "Meditation", "Yoga Nidra",
    "Mindful Nutrition", "Habit Mastery", "Vinyasa Krama",
  ],
};

export default function About() {
  return (
    <div data-testid="about-page" className="section-pad">
      <SEO
        title="Best Yoga Coach for Weight Loss — Dhruva Lingappa | Weight Loss Gurukul"
        description="Lost 110 lbs (50 kg) and reversed hypothyroidism — Dhruva Lingappa is the best 1-on-1 online yoga coach for weight loss, trained in the T. Krishnamacharya lineage. Ancient Wisdom · Real Transformation."
        path="/about"
        keywords="best yoga coach for weight loss, Dhruva Lingappa, online yoga teacher 1 on 1, weight loss yoga coach India, Vinyasa Krama yoga, Krishnamacharya tradition, hypothyroid yoga, ancient wisdom yoga"
        jsonLd={PERSON_JSON_LD}
      />
      <div className="container-x">
        {/* Hero */}
        <div className="max-w-3xl">
          <p className="eyebrow">Our origins</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl text-forest leading-[1.05]">
            A Gurukul, reimagined for the <em className="italic text-clay">modern seeker.</em>
          </h1>
        </div>

        {/* What is a Gurukul */}
        <div className="mt-10 md:mt-14 grid lg:grid-cols-5 gap-10 md:gap-14 items-start">
          <div className="lg:col-span-3 space-y-5 text-ink-soft leading-relaxed">
            <p>
              <strong className="text-forest font-medium">Gurukul</strong> is the ancient Vedic system of education. The word combines two Sanskrit terms — <em>Guru</em> (teacher, mentor, spiritual guide) and <em>Kula</em> (family, home, community).
            </p>
            <p>
              In ancient India, a student would leave their ordinary life and enter a complete transformation under the direct, personal mentorship of a Guru. Learning was never transactional. It was comprehensive — body, mind, spirit, habit and character — all shaped together within one sacred relationship.
            </p>
            <p>
              That is the tradition Weight Loss Gurukul is built upon. Thousands of years ago, a student would travel great distances to find their Guru. Today, the Gurukul comes to you — privately, personally, and powerfully.
            </p>
          </div>
          <aside className="lg:col-span-2 rounded-3xl border border-sand-300 bg-sand-200/40 p-6 md:p-7">
            <Sparkles className="h-5 w-5 text-clay" />
            <p className="mt-3 font-serif text-lg italic text-forest leading-snug">
              "When the student is ready — the Guru appears."
            </p>
            <p className="mt-2 text-sm text-ink-soft">
              An invitation to begin your transformation.
            </p>
          </aside>
        </div>

        {/* Coach panel */}
        <div className="mt-20 md:mt-24 grid lg:grid-cols-2 gap-10 md:gap-14 items-start">
          <div className="rounded-[2rem] overflow-hidden aspect-[4/5] border border-sand-300 shadow-xl bg-sand-200">
            <img src={DHRUVA_IMG} alt="Dhruva Lingappa, Founder of Weight Loss Gurukul" className="h-full w-full object-cover" />
          </div>

          <div>
            <p className="eyebrow">Meet your coach</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-forest leading-tight">
              Dhruva Lingappa — India's leading <em className="italic text-clay">transformation maestro.</em>
            </h2>

            <div className="mt-6 space-y-4 text-ink-soft leading-relaxed">
              <p>
                Namaste — I'm Dhruva. For years I lived with morbid obesity (40+ BMI) and a hypothyroid condition. Diet after diet, gym after gym, programme after programme — none of it moved the needle. The only thing that got lighter was my wallet.
              </p>
              <p>
                I turned my home into a laboratory. I experimented with nutrition, movement, mindset and breath. The result: a 110 lb (50 kg) loss, off thyroid medication since 2011, and a complete inner transformation. The breakthrough didn't come from modern fitness — it came from ancient timeless wisdom, practised as one unified, living system.
              </p>
              <p>
                Yogasana · Pranayama · Meditation · Yoga Nidra · Mindful Nutrition · Habit Mastery — taught not as isolated techniques, but as a single integrated path.
              </p>
              <p>
                One question consumed me afterwards: <em>How do I make this repeatable — for everyone?</em> The answer became Weight Loss Gurukul. It's how I share the exact methodology with the world, where ancient holistic science meets private 1-on-1 guidance.
              </p>
            </div>

            <a
              href={TIMES_OF_INDIA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-page-toi-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white/80 px-4 py-2 text-xs text-ink-soft hover:border-forest/40 hover:text-forest transition-colors"
            >
              <Award className="h-3.5 w-3.5 text-clay" />
              Featured in <strong className="text-forest font-medium">The Times of India</strong>
              <ExternalLink className="h-3 w-3" />
            </a>

            <h3 className="mt-10 font-serif text-2xl text-forest">Lineage & training</h3>
            <ul className="mt-4 grid gap-3">
              {CREDENTIALS.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-ink-soft leading-relaxed rounded-2xl border border-sand-300 bg-white p-4">
                  <CheckCircle2 className="h-5 w-5 text-forest shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waLink(WA_MESSAGES.trial)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-page-whatsapp-cta"
                className="btn-accent"
              >
                <MessageCircle className="h-4 w-4" /> Message Dhruva
              </a>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="about-page-call-cta"
                className="btn-outline"
              >
                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>

        {/* Before / after */}
        <div className="mt-20 md:mt-24">
          <p className="eyebrow">Living proof</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl text-forest leading-tight">
            264 lbs – 154 lbs <span className="text-clay">(Lost 110 lbs)</span><br/>
            <em className="italic text-clay">120 kgs – 70 kgs <span className="not-italic">(Lost 50 kgs)</span></em>
          </h2>
          <p className="mt-4 text-ink-soft max-w-2xl">
            The protocol you'll follow is the one Dhruva engineered on himself — refined over a decade of practice in the Vinyasa-Krama tradition.
          </p>
          <div className="mt-8 rounded-3xl overflow-hidden border border-sand-300 bg-white">
            <img
              src={BEFORE_AFTER}
              alt="Before and after transformation of Dhruva Lingappa — 264 lbs (120 kg) to 154 lbs (70 kg)"
              className="w-full h-auto block min-h-[300px] sm:min-h-[420px] md:min-h-[560px] object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
